import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import envConfigs from '../configs/envConfigs'
import { DETAILS_APPLY_FOR, DIMENSION_UNIT, calculateDensity, WEIGHT_UNIT } from '../constants'
import { portalAuthHttpClient } from '../utils/HttpRequestUtils'
import { calculateWeightToKg, calculateDimensionToCm } from '../utils/common'
import getCurrentUser from '../utils/getCurrentUser'
import { getShipmentClassificationRequest } from '../utils/shipmentClassification'

export const getSlrBookingDetailsResponse = async (slrNumber, customerCode = '') => {
  const url = `${envConfigs.baseUrl}${envConfigs.getSLRBookingDetailsUrl}/${slrNumber}`
  const headers = customerCode ? { 'slr-customer-code': customerCode } : {}
  const response = await portalAuthHttpClient.get(url, { headers })
  return response
}

export const updateSlrStatus = async (status, customerCode, slrId, rejectionReason) => {
  const url = `${envConfigs.baseUrl}${envConfigs.updateSlrStatusUrl.replace('{slrId}', slrId)}`
  const payload = {
    customerCode,
    slrStatus: status,
    rejectionReason: rejectionReason || '',
  }
  const headers = customerCode ? { 'slr-customer-code': customerCode } : {}

  const response = await portalAuthHttpClient.post(url, payload, { headers })
  return response
}

export const submitSlrUpdate = async (slrNumber, slrUpdatePayload) => {
  const url = `${envConfigs.baseUrl}${envConfigs.updateSlr.replace('{slrId}', slrNumber)}`
  const { customerCode } = slrUpdatePayload || {};
  const headers = customerCode ? { 'slr-customer-code': customerCode } : {}
  const response = await portalAuthHttpClient.post(url, slrUpdatePayload, { headers })
  return response
}

export const saveSlrAttachment = async (file, attachmentId) => {
  const formData = new FormData()
  formData.append('attachmentId', attachmentId)
  formData.append('multipartFile', file)
  const url = `${envConfigs.baseUrl}${envConfigs.saveSlrAttachment}`
  const response = await portalAuthHttpClient.post(url, formData)
  if (response?.data?.status !== 'SUCCESS' || !response.data.attachmentId) {
    throw new Error('Failed to upload file')
  }
  return response.data
}

// Converts a File to the standard Base64 string the SLR attachment API expects.
const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
  reader.onerror = reject
  reader.readAsDataURL(file)
})

const mapSlrAttachment = async (attachment) => {
  if (attachment.attachmentId) {
    return {
      id: attachment.id ?? null,
      attachmentId: attachment.attachmentId,
      fileName: attachment.fileName || attachment.name,
      url: attachment.url,
      fileType: attachment.fileType || attachment.filetype,
    }
  }
  if (attachment.id != null) {
    return { id: attachment.id }
  }
  if (attachment instanceof File) {
    return {
      fileName: attachment.name,
      contentType: attachment.type,
      contentBase64: await readFileAsBase64(attachment),
    }
  }
  // Existing attachment being retained on an update: keep only its id.
  return { id: attachment.id }
}

const mapSlrDimensionDetail = (row) => {
  const pieces = Number(row.pieces) || 0
  const rowWeight = Number(row.weight) || 0
  const weightPerPiece =
    row.weightType === DETAILS_APPLY_FOR.TOTAL && pieces > 0
      ? rowWeight / pieces
      : rowWeight

  return {
    pieces,
    length: Number(row.length) || 0,
    width: Number(row.width) || 0,
    height: Number(row.height) || 0,
    dimensionUnit: row.dimensionUnit || 'C',
    weightPerPiece: Number(weightPerPiece.toFixed(3)),
    weightPerPieceUnit: row.weightUnit || 'K',
    nonStackable: row.stackable === false,
    nonTiltable: row.turnable !== true,
    nonCraneLiftable: row.craneLiftable === false,
    nonForkLiftable: row.forkLiftable === false,
    fragile: row.fragile === true,
    packingType: row.packingType || null,
  }
}

const calculateSlrVolume = (rows) => rows.reduce((totalVolume, row) => {
  const length = Number(row.length) || 0
  const width = Number(row.width) || 0
  const height = Number(row.height) || 0
  const pieces = Number(row.pieces) || 0
  const dimensionUnit = row.dimensionUnit || DIMENSION_UNIT.CENTIMETERS

  let rowVolume = 0.01

  if (length && width && height && !isNaN(length) && !isNaN(width) && !isNaN(height)) {
    if (dimensionUnit === DIMENSION_UNIT.INCHES) {
      rowVolume = parseFloat((width * 0.0254) * (length * 0.0254) * (height * 0.0254)) * pieces
    } else if (dimensionUnit === DIMENSION_UNIT.CENTIMETERS) {
      rowVolume = parseFloat((width * 0.01) * (length * 0.01) * (height * 0.01)) * pieces
    }
  }

  if (rowVolume && rowVolume < 0.01) {
    rowVolume = 0.01
  }

  return totalVolume + rowVolume
}, 0)

// Maps internal dimension unit codes to CargoStack API format
const mapDimensionUnitToCargoStackFormat = (internalUnit) => {
  const unitMap = {
    'C': 'cm',      // Centimeters
    'CM': 'cm',
    'I': 'in',      // Inches
    'IN': 'in',
    'M': 'm',       // Meters
    'MM': 'mm',     // Millimeters
  }
  return unitMap[internalUnit] || 'cm' // Default to cm
}

// Maps internal weight unit codes to CargoStack API format
const mapWeightUnitToCargoStackFormat = (internalUnit) => {
  const unitMap = {
    'K': 'kg',      // Kilogram
    'KG': 'kg',
    'G': 'g',       // Grams
    'LB': 'lb',     // Pounds
    'T': 'T',       // Tons/Tonnes
  }
  return unitMap[internalUnit] || 'kg' // Default to kg
}

export const submitSlrCargoStack = async (slrResponse, shipment) => {
  const { slrReferenceNumber: slrReferenceId, customerCode } = slrResponse
  if (!slrReferenceId) {
    throw new Error('SLR reference number is required for CargoStack')
  }

  const {
    origin,
    destination,
    shippingItem,
    totalWeight,
    weightUnit = WEIGHT_UNIT.KILOGRAM,
    packagingRows = [],
    shippingDescription,
    sccCodes = [],
    transitAirports = [],
  } = shipment

  const originCode = typeof origin === 'string' ? origin : origin?.code
  const destinationCode = typeof destination === 'string' ? destination : destination?.code
  const dimensionDetails = packagingRows.map(mapSlrDimensionDetail).map(detail => ({
    pieces: detail.pieces,
    length: Number(calculateDimensionToCm(detail.length, detail.dimensionUnit)),
    width: Number(calculateDimensionToCm(detail.width, detail.dimensionUnit)),
    height: Number(calculateDimensionToCm(detail.height, detail.dimensionUnit)),
    weightPerPiece: Number(calculateWeightToKg(detail.weightPerPiece, detail.weightPerPieceUnit)),
    isStackable: !detail.nonStackable,
    isTiltable: !detail.nonTiltable,
  }))

  const request = {
    requestId: uuidv4(),
    slrReferenceId,
    loadabilityDetails: {
      origin: originCode?.toUpperCase(),
      destination: destinationCode?.toUpperCase(),
      aircraftTypeCode: '77X',
      carrierCode: 'EY',
      pieceCount: dimensionDetails.reduce((total, detail) => total + detail.pieces, 0),
      totalWeight: Number(calculateWeightToKg(Number(totalWeight), weightUnit)),
      totalVolume: Number(calculateSlrVolume(packagingRows).toFixed(2)),
      dimensionUnit: mapDimensionUnitToCargoStackFormat('cm'),
      weightUnit: mapWeightUnitToCargoStackFormat(WEIGHT_UNIT.KILOGRAM),
      dimensionDetails,
      sccCodes: [...new Set([
        shippingItem?.scc,
        ...sccCodes.map(item => item.scc),
      ].filter(Boolean))].join(','),
      commodity: shippingItem?.code,
      product: shippingItem?.product,
      agentCode: customerCode,
      shipmentDescription: shippingDescription,
      transitPoints: transitAirports.map(transit => transit.airportCode),
    },
  }
  const url = `${envConfigs.baseUrl}${envConfigs.cargoStackAddUpdateUrl}`
  return portalAuthHttpClient.post(url, request, {
    headers: { 'slr-customer-code': customerCode },
  })
}

// Maps the SLR form UI state to the backend SaveSpecialLoadRequestRequest shape.
const mapSaveSlrRequestBody = async (formDataBody, userDetails, customerCode) => {
  const {
    origin,
    destination,
    shippingDate,
    shippingItem,
    totalWeight,
    weightUnit,
    packagingRows = [],
    isStackable,
    isTiltable,
    isCraneLiftable,
    isForkLiftable,
    isFragile,
    shippingDescription,
    airwayBillNumber,
    attachments = [],
    sccCodes = [],
    transitAirports = [],
    slrReferenceNumber,
    slrId,
  } = formDataBody


  const originCode = typeof origin === 'string' ? origin : origin?.code
  const destinationCode = typeof destination === 'string' ? destination : destination?.code
  const specialHandlingCode = sccCodes.map((item) => item.scc).join(',')

  // Calculate total pieces count
  const totalPieces = packagingRows.reduce(
    (total, row) => total + (Number(row.pieces) || 0),
    0
  )

  // Calculate total volume in cubic meters (exactly matching booking flow)
  const totalVolumeInMtrs = calculateSlrVolume(packagingRows)

  // Calculate density (kg/m³) - matching booking flow
  const density = calculateDensity(totalVolumeInMtrs, Number(calculateWeightToKg(Number(totalWeight) || 0, weightUnit || WEIGHT_UNIT.KILOGRAM)) || 0)

  return {
    origin: originCode?.toUpperCase(),
    destination: destinationCode?.toUpperCase(),
    shippingDate: shippingDate
      ? moment(shippingDate, ['DD-MMM-YYYY', moment.ISO_8601]).format('YYYY-MM-DD')
      : null,
    product: shippingItem?.product,
    commodity: shippingItem?.code,
    primaryScc: shippingItem?.scc,
    specialHandlingCode: specialHandlingCode || null,
    grossWeight: Number(totalWeight) || 0,
    grossWeightUnit: weightUnit || WEIGHT_UNIT.KILOGRAM,
    stackable: isStackable === true,
    tiltable: isTiltable === true,
    craneLiftable: isCraneLiftable === true,
    forkLiftable: isForkLiftable === true,
    fragile: isFragile === true,
    totalPieces,
    volume: Number(totalVolumeInMtrs).toFixed(2),
    volumeUnit: DIMENSION_UNIT.METERS,
    density,
    customerCode,
    customerName: userDetails?.companyName || null,
    awbNumber: airwayBillNumber || null,
    shipmentDescription: shippingDescription || null,
    dimensionDetails: packagingRows.map(mapSlrDimensionDetail),
    attachments: await Promise.all(attachments.map(mapSlrAttachment)),
    submittedBy: {
      name: userDetails?.companyName || null,
      email: userDetails?.email || null,
      phoneNumber: userDetails?.telephoneNo || userDetails?.mobileNo || null,
    },
    transitAirports: transitAirports || [],
    ...(slrReferenceNumber && { slrReferenceNumber }),
    ...(slrId && { slrId }),
  }
}

export const submitSlrBooking = async (formDataBody, isGSA, slrId = null, validatedAgentDetails = null) => {
  let userDetails = getCurrentUser();
  if (isGSA && validatedAgentDetails) {
    userDetails = {
      ...userDetails, companyName: validatedAgentDetails?.companyName || validatedAgentDetails?.userName,
      email: validatedAgentDetails?.email,
      telephoneNo: validatedAgentDetails?.contactNo,
      mobileNo: validatedAgentDetails?.contactNo,
    }
  }
  const customerCode = isGSA ? formDataBody?.customerCode : userDetails?.customerCode || ''
  const data = await mapSaveSlrRequestBody(formDataBody, userDetails, customerCode)

  // Handle update scenario (isEditMode)
  if (slrId) {
    const url = `${envConfigs.baseUrl}${envConfigs.submitSlrBooking}/${slrId}`
    const response = await portalAuthHttpClient.post(url, data, {
      headers: {
        'slr-customer-code': customerCode,
      },
    })
    return response
  }

  // Handle create scenario
  const url = `${envConfigs.baseUrl}${envConfigs.submitSlrBooking}`
  const response = await portalAuthHttpClient.post(url, data, {
    headers: {
      'slr-customer-code': customerCode,
    },
  })
  return response
}

export const getSLRListBookings = async (requestBody) => {
  const { agentCode } = requestBody
  const url = `${envConfigs.baseUrl}${envConfigs.getSLRListUrl}`
  const config = {}
  if (agentCode) {
    config.headers = {
      'slr-customer-code': agentCode,
    }
  }
  const response = await portalAuthHttpClient.post(url, requestBody, config)
  return response
}

/**
 * Classifies shipment based on packaging details
 * Returns simple classification response with type and chargeable weight
 * @param {Array} packagingRows - Array of packaging row details
 * @returns {Promise} Classification response {type: "SLR"|"ODF"|"ODP"|"NORMAL", chargeableWeight: number|null}
 */
export const classifyShipment = async (packagingRows = [], agentCode, options = {}) => {
  try {
    if (!packagingRows || packagingRows.length === 0) {
      console.warn('No packaging rows provided for classification')
      return null
    }

    const request = {
      ...getShipmentClassificationRequest(packagingRows, options),
      requestId: uuidv4(),
    }

    const hasValidRows = request.dimensionDetails.every(row =>
      Number.isInteger(row.pieceCount) && row.pieceCount > 0 &&
      ['lengthCm', 'widthCm', 'heightCm', 'weightPerPieceKg'].every(key => Number.isFinite(row[key]) && row[key] >= 0.001)
    ) && Number.isFinite(request.totalWeight) && request.totalWeight > 0 && ['I', 'U'].includes(request.operationFlag)

    if (!hasValidRows) {
      console.warn('Invalid packaging rows for classification')
      return null
    }

    const url = `${envConfigs.baseUrl}${envConfigs.classifyUserInput}`
    const response = await portalAuthHttpClient.post(url, request, {
      headers: {
        'slr-customer-code': agentCode || '',
      },
    })

    return response?.data || null
  } catch (error) {
    console.error('Error classifying shipment:', error)
    return null
  }
}

